/**
 * Naming a camera the operator is registering.
 *
 * A local capture device already auto-fills its name from the device label, and
 * a discovered network camera should behave the same way — typing a name by
 * hand is the last piece of manual setup left in the ORDRO flow, and it is the
 * one most likely to produce "Camera", "camera2", "ordro new" across a fleet.
 *
 * Two rules:
 *   - derive a sensible base name from what the agent detected;
 *   - never collide with a camera already on that appliance — suffix _1, _2, …
 *     so a second identical camera (two EP8s in one room, two identical USB
 *     cams) registers without the operator having to invent a distinguisher.
 */

/** Trailing " (…)" that the agent appends for disambiguation, e.g. the IP. */
const TRAILING_PAREN = /\s*\([^()]*\)\s*$/;

/**
 * The name to offer for a detected camera.
 *
 * The agent labels a discovered network camera "<SSID> (<ip>)" — the SSID is
 * what is printed on the camera itself and is unique per unit, so it makes a
 * far better name than the IP, which changes whenever the access point is
 * rebuilt. Local devices keep their label as-is.
 */
export function baseCameraName(label: string): string {
  const trimmed = (label ?? '').trim();
  const withoutHost = trimmed.replace(TRAILING_PAREN, '').trim();
  return withoutHost || trimmed;
}

/**
 * `base`, or the first free `base_N`, given the names already registered.
 *
 * Comparison is case-insensitive and whitespace-trimmed: "EP8" and "ep8 " are
 * the same camera to a human reading the list, so treating them as distinct
 * would defeat the point.
 */
export function uniqueCameraName(base: string, existing: readonly string[]): string {
  const want = (base ?? '').trim();
  if (!want) return want;
  const taken = new Set(
    existing.map((n) => (n ?? '').trim().toLowerCase()).filter(Boolean),
  );
  if (!taken.has(want.toLowerCase())) return want;
  // Unbounded in principle; in practice an OR has a handful of cameras, and a
  // runaway loop here would be a worse failure than a duplicate name.
  for (let i = 1; i < 1000; i += 1) {
    const candidate = `${want}_${i}`;
    if (!taken.has(candidate.toLowerCase())) return candidate;
  }
  return want;
}

/**
 * Name to pre-fill for a detected camera: its base name, de-duplicated against
 * what is already registered on that appliance.
 */
export function suggestedCameraName(
  label: string,
  existing: readonly string[],
): string {
  return uniqueCameraName(baseCameraName(label), existing);
}

/**
 * Guess the camera type from what was detected, so the operator does not set a
 * dropdown that the SSID already answered. The ORDRO's access point announces
 * itself as EP…; anything else stays "generic" rather than guessing wrong.
 */
export function guessCameraType(label: string): 'ordro' | 'generic' {
  // EP followed by a digit or hyphen — the EP-series access-point shape
  // (EP8-A1B2C3, EP-1234). A looser "starts with EP" also swallows ordinary
  // words like "Epson", which would silently mislabel an unrelated camera.
  return /^EP[-\d]/i.test(baseCameraName(label)) ? 'ordro' : 'generic';
}
